import React from "react";
import { motion } from "framer-motion";
import { Check, Plus, Loader2, Wand2 } from "lucide-react";
import { useHOH, STEP } from "../../../context/StyleContext";
import { pickImage } from "../../../data/looks";
import StepFrame, { StepTitle } from "../StepFrame";

function ItemCard({ item, i, kind, selected, onToggle, direction }) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 4) * 0.05 }}
      className={`hoh-card p-3 flex gap-3 items-center ${selected ? "hoh-glow-border" : ""}`} data-testid={`${kind}-item-${i}`}>
      <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 bg-hoh-cream">
        <img src={pickImage(item.category || item.name, kind === "accessory" ? "accessory" : "cosmetic", direction)} alt={item.name} className="w-full h-full object-cover" />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <span className="text-[10px] uppercase tracking-wider text-hoh-rose font-semibold">{item.category}</span>
        <h4 className="text-sm font-semibold text-black truncate">{item.name}</h4>
        <div className="flex items-center justify-between mt-1">
          <span className="text-sm font-medium text-black">{item.price}</span>
          <span className="text-[10px] text-hoh-slate">{item.store}</span>
        </div>
      </div>
      <button onClick={() => onToggle(item)} className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-hoh-rose text-hoh-navy" : "bg-hoh-navy text-white hover:bg-[#2D3A54]"}`} data-testid={`${kind}-add-${i}`}>
        {selected ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      </button>
    </motion.div>
  );
}

export default function AccessoryProductsStep() {
  const { accessories, products, loadingAP, selectedAccessories, toggleAccessory, selectedProducts, toggleProduct, styleDirection, runGenerateLook, goTo } = useHOH();

  const proceed = () => { runGenerateLook(); goTo(STEP.FINAL); };

  return (
    <StepFrame wide className="text-center">
      <StepTitle overline="Step 5" title="Accessories & grooming" subtitle="Add the finishing touches — only what suits your look and occasion." />

      {loadingAP ? (
        <div className="py-20 flex flex-col items-center"><Loader2 className="w-8 h-8 text-hoh-rose animate-spin" /><p className="mt-3 text-hoh-slate text-sm">Curating your extras…</p></div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <div>
              <h3 className="text-xl font-semibold text-black mb-4">Accessories</h3>
              <div className="space-y-3">
                {accessories.map((a, i) => <ItemCard key={a.name + i} item={a} i={i} kind="accessory" direction={styleDirection} selected={!!selectedAccessories.find((x) => x.name === a.name)} onToggle={toggleAccessory} />)}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-4">Grooming Products</h3>
              <div className="space-y-3">
                {products.map((p, i) => <ItemCard key={p.name + i} item={p} i={i} kind="product" direction={styleDirection} selected={!!selectedProducts.find((x) => x.name === p.name)} onToggle={toggleProduct} />)}
              </div>
            </div>
          </div>
          <div className="mt-12">
            <button onClick={proceed} className="btn-royal" data-testid="generate-final-btn">
              <Wand2 className="w-4 h-4" /> Generate Final Look
            </button>
            <p className="mt-3 text-xs text-hoh-slate">Extras are optional — you can continue anytime.</p>
          </div>
        </>
      )}
    </StepFrame>
  );
}
